import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/utils/checkout";
import { TOOL_ENTITLEMENT, TOOL_NAME, UNLOCK_TIER, userHas } from "@/utils/entitlements";
import { toast } from "sonner";
import { KeyRound, AlertCircle, ArrowLeft, Lock, Sparkles } from "lucide-react";
import { trackEvent } from "@/hooks/useAnalytics";

const TOOL_MAP = {
  "idea-evaluator": { file: "idea-evaluator.html", title: "Viral Idea Evaluator" },
  "book-studio": { file: "book-studio.html", title: "Viral Book Studio" },
  "cover-studio": { file: "cover-studio.html", title: "Viral Book Cover Studio" },
};

export default function ToolFrame() {
  const { slug } = useParams();
  const tool = TOOL_MAP[slug];
  const { user } = useAuth();
  const iframeRef = useRef(null);
  const [keyReady, setKeyReady] = useState(null);
  const [buying, setBuying] = useState(false);

  const entitlement = TOOL_ENTITLEMENT[slug];
  const hasEntitlement = userHas(entitlement, user);

  useEffect(() => {
    if (!hasEntitlement) return;  // skip key probe if tool is locked
    // Fire-and-forget — record this tool launch for analytics
    trackEvent({ type: "tool_launch", tool: slug, path: window.location.pathname });
    let cancelled = false;
    (async () => {
      try {
        const cached = localStorage.getItem("vbs_anthropic_key");
        if (cached) {
          if (!cancelled) setKeyReady(true);
        } else {
          const { data } = await api.get("/api/settings/anthropic-key");
          if (!data.has_anthropic_key) {
            if (!cancelled) setKeyReady(false);
          } else {
            const { data: reveal } = await api.get("/api/settings/anthropic-key/reveal");
            localStorage.setItem("vbs_anthropic_key", reveal.api_key);
            if (!cancelled) setKeyReady(true);
          }
        }
        // Also mirror the OpenAI key (server → localStorage) so the iframe has
        // it immediately for image generation. Fire-and-forget — never blocks.
        try {
          if (!localStorage.getItem("vbs_openai_key")) {
            const { data: oaiStatus } = await api.get("/api/settings/openai-key");
            if (oaiStatus && oaiStatus.has_openai_key) {
              const { data: oaiReveal } = await api.get("/api/settings/openai-key/reveal");
              if (oaiReveal && oaiReveal.api_key) {
                localStorage.setItem("vbs_openai_key", oaiReveal.api_key);
              }
            }
          }
        } catch (oaiErr) {}
      } catch (e) {
        if (!cancelled) setKeyReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, hasEntitlement]);

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev?.data?.type === "VBS_TOOL_READY" && iframeRef.current) {
        const key = localStorage.getItem("vbs_anthropic_key");
        if (key) iframeRef.current.contentWindow?.postMessage({ type: "VBS_SET_KEY", key }, "*");
        const oaiKey = localStorage.getItem("vbs_openai_key");
        if (oaiKey) iframeRef.current.contentWindow?.postMessage({ type: "VBS_SET_OPENAI_KEY", key: oaiKey }, "*");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  if (!tool) {
    return (
      <Shell>
        <div className="vbs-page-eye">ERROR</div>
        <h1 className="vbs-page-h">Tool not found</h1>
        <Link to="/dashboard" className="vbs-btn vbs-btn-gold">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>
      </Shell>
    );
  }

  if (!hasEntitlement) {
    const unlockTierId = UNLOCK_TIER[entitlement] || "suite";
    const handleUnlock = async () => {
      setBuying(true);
      try { await startCheckout(unlockTierId); }
      catch (e) { toast.error("Could not start checkout"); setBuying(false); }
    };
    return (
      <Shell>
        <div className="vbs-page-eye">{tool.title.toUpperCase()}</div>
        <h1 className="vbs-page-h">
          <em>{tool.title}</em> is locked
        </h1>
        <p className="vbs-page-p">
          Your current plan doesn't include {TOOL_NAME[slug]}. Upgrade once and unlock it permanently — no subscriptions.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleUnlock} disabled={buying} className="vbs-btn vbs-btn-gold" data-testid="tool-unlock-btn">
            <Sparkles size={14} /> {buying ? "Loading checkout…" : `Unlock — ${unlockTierId === "studio" ? "$97" : unlockTierId === "suite" ? "$497" : unlockTierId === "vip" ? "$997" : "$1,997"}`}
          </button>
          <Link to="/dashboard" className="vbs-btn vbs-btn-ghost">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
        <div className="vbs-card" style={{ marginTop: 30, padding: 22, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Lock size={20} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ color: "var(--mid)", fontSize: 13.5, lineHeight: 1.7 }}>
            Want the complete suite? <Link to="/#pricing">Compare all tiers</Link> — there's
            a one-time package for every stage, from solo self-starter to hands-on cohort.
          </div>
        </div>
      </Shell>
    );
  }

  if (keyReady === false) {
    return (
      <Shell>
        <div className="vbs-page-eye">{tool.title.toUpperCase()}</div>
        <h1 className="vbs-page-h">
          Connect your <em>Anthropic</em> key first
        </h1>
        <p className="vbs-page-p">
          {tool.title} runs on Claude Sonnet 4.5 using your own API key. Add yours in Settings and the tool will light up instantly.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <Link to="/settings" className="vbs-btn vbs-btn-gold" data-testid="tool-connect-key">
            <KeyRound size={14} /> Open Settings
          </Link>
          <Link to="/dashboard" className="vbs-btn vbs-btn-ghost">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
        <div className="vbs-card" style={{ marginTop: 30, padding: 22, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <AlertCircle size={20} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ color: "var(--mid)", fontSize: 13.5, lineHeight: 1.7 }}>
            Get an Anthropic API key at{" "}
            <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
              console.anthropic.com
            </a>
            . Your key is encrypted at rest and never shared. You control spend on your own account.
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell fullBleed>
      <div className="vbs-iframe-wrap">
        {keyReady && (
          <iframe
            ref={iframeRef}
            src={`/tools/${tool.file}`}
            title={tool.title}
            data-testid={`tool-iframe-${slug}`}
            allow="clipboard-read; clipboard-write; fullscreen"
          />
        )}
      </div>
    </Shell>
  );
}
